import { useEffect, useState, useMemo } from "react";
import { api, formatApiError, fmtMoney, logoSrc } from "../../lib/api";
import { toast } from "sonner";
import { useLeague } from "./LeagueLayout";
import { Building2, ArrowUpCircle, Coins, TrendingUp, Wrench, Lock, Star, History } from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";

const money = (v) => fmtMoney(Math.round(Number(v || 0)));

export default function LeagueStadio() {
  const { league, myTeam, teams, isAdmin, reload: reloadLeague } = useLeague();
  const [financeData, setFinanceData] = useState(null);
  const [history, setHistory] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState(myTeam?.team_id || "");
  const [upgradeBusy, setUpgradeBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [finR, histR] = await Promise.all([
        api.get(`/leagues/${league.league_id}/finances`),
        api.get(`/leagues/${league.league_id}/history`),
      ]);
      setFinanceData(finR.data);
      setHistory(histR.data || []);
      if (!selectedTeamId && finR.data.teams?.length) {
        setSelectedTeamId(myTeam?.team_id || finR.data.teams[0].team_id);
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [league.league_id]);

  const stadiumLevels = league.settings?.stadium_levels || [];
  const selectedTeam = teams.find(t => t.team_id === selectedTeamId);
  const selectedFinance = financeData?.teams?.find(t => t.team_id === selectedTeamId);
  const currentLevel = Number(selectedTeam?.stadium_level || 0);
  const nextLevelIdx = currentLevel; // next level array index
  const nextLevel = stadiumLevels[nextLevelIdx];
  const isMax = currentLevel >= stadiumLevels.length;

  const canManageTeam = isAdmin || (selectedTeam?.manager_id === myTeam?.manager_id && myTeam?.team_id === selectedTeamId);
  const [adminBusy, setAdminBusy] = useState(false);

  async function forceSetLevel(newLevel) {
    if (!isAdmin) return;
    if (!window.confirm(`⚙️ Modifica ADMIN: imposta stadio "${selectedTeam.name}" al livello ${newLevel} (bypass costi/fase)?`)) return;
    setAdminBusy(true);
    try {
      await api.patch(`/leagues/${league.league_id}/teams/${selectedTeam.team_id}`, { stadium_level: newLevel });
      toast.success(`Stadio di ${selectedTeam.name} → Livello ${newLevel}`);
      await reloadLeague();
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setAdminBusy(false); }
  }

  const doUpgrade = async () => {
    if (!nextLevel) return;
    if (!window.confirm(`Confermi l'upgrade allo stadio livello ${nextLevel.level}? Costo: ${money(nextLevel.cost)} cr.`)) return;
    setUpgradeBusy(true);
    try {
      await api.post(`/leagues/${league.league_id}/teams/${selectedTeamId}/stadium/upgrade`);
      toast.success(`🏗️ Stadio upgradato al livello ${nextLevel.level}!`);
      await reloadLeague();
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUpgradeBusy(false); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] tracking-[0.3em] text-orange-400 uppercase font-black flex items-center gap-2">
            <Building2 size={14}/> Modulo Stadio
          </div>
          <h2 className="text-3xl font-black tracking-tight mt-1">🏟️ Il tuo Stadio</h2>
          <p className="text-slate-400 text-sm mt-1">Impianto sportivo: ricavo per stagione, manutenzione, upgrade e bonus configurabili.</p>
        </div>
        <select
          data-testid="stadium-team-select"
          value={selectedTeamId || ""}
          onChange={(e)=>setSelectedTeamId(e.target.value)}
          className="bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm font-bold text-white focus:outline-none focus:border-orange-500"
        >
          {teams.map(t => <option key={t.team_id} value={t.team_id}>{t.name}</option>)}
        </select>
      </div>

      {loading || !selectedTeam ? (
        <div className="text-slate-500 text-sm py-8 text-center">Caricamento…</div>
      ) : (
        <>
          {/* Current stadium hero card */}
          <div className="border border-orange-500/30 rounded-3xl bg-gradient-to-br from-orange-900/30 via-slate-900 to-slate-950 p-6 sm:p-8 shadow-2xl shadow-black/40 relative overflow-hidden" data-testid="current-stadium">
            <div className="absolute -right-8 -top-8 text-[200px] opacity-5 select-none">🏟️</div>
            <div className="relative z-10 flex items-start justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                {logoSrc(selectedTeam.logo_url)
                  ? <img src={logoSrc(selectedTeam.logo_url)} alt="" className="w-16 h-16 rounded-2xl object-cover border border-slate-700"/>
                  : <div className="w-16 h-16 rounded-2xl bg-slate-800 flex items-center justify-center text-3xl">⚽</div>}
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-orange-300 font-black">Stadio · {selectedTeam.name}</div>
                  <h3 className="text-3xl font-black mt-1">
                    {currentLevel > 0 ? `Livello ${currentLevel}` : "Nessuno stadio"}
                    {stadiumLevels[currentLevel-1]?.name && <span className="text-slate-500 text-lg ml-2 font-normal">&quot;{stadiumLevels[currentLevel-1].name}&quot;</span>}
                  </h3>
                  <div className="text-sm text-slate-400 mt-1">
                    Capienza {currentLevel} / {stadiumLevels.length} · {isMax ? "🏆 Massimo raggiunto" : `Prossimo: Livello ${currentLevel + 1}`}
                  </div>
                </div>
              </div>
              {!isMax && canManageTeam && (
                <button
                  onClick={doUpgrade} disabled={upgradeBusy}
                  data-testid="upgrade-stadium-btn"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-orange-500 hover:bg-orange-400 text-slate-950 font-black text-sm uppercase tracking-wider shadow-lg shadow-orange-500/30"
                >
                  <ArrowUpCircle size={16}/> {upgradeBusy ? "Upgrade in corso…" : `Upgrade (${money(nextLevel?.cost)} cr)`}
                </button>
              )}
            </div>

            {/* KPI economici stadio */}
            {selectedFinance && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
                <StadiumKpi icon={<Coins size={14}/>} label="Ricavo base" value={money(selectedFinance.stadium.base_income)} tone="emerald"/>
                <StadiumKpi icon={<Wrench size={14}/>} label="Manutenzione" value={money(selectedFinance.stadium.maintenance)} tone="rose" prefix="-"/>
                <StadiumKpi icon={<Star size={14}/>} label="Bonus applicati" value={`+${(selectedFinance.stadium.bonus_multiplier * 100).toFixed(0)}%`} tone="amber"/>
                <StadiumKpi icon={<TrendingUp size={14}/>} label="Ricavo NETTO" value={money(selectedFinance.stadium.net_income)} tone="emerald" big/>
              </div>
            )}

            {/* Bonus dettaglio */}
            {selectedFinance?.stadium?.bonus_applied?.length > 0 && (
              <div className="mt-5 rounded-2xl bg-slate-950/60 border border-slate-800 p-4">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">✨ Bonus stadio attivi</div>
                <div className="flex flex-wrap gap-2">
                  {selectedFinance.stadium.bonus_applied.map((b, i) => (
                    <span key={i} className="text-[11px] px-2.5 py-1 rounded-full bg-amber-500/15 border border-amber-500/40 text-amber-300 font-bold" data-testid={`stadium-bonus-${i}`}>
                      {b.label} · +{(b.multiplier * 100).toFixed(0)}%
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Timeline livelli */}
          <div className="border border-slate-800 rounded-3xl bg-gradient-to-br from-slate-900 to-slate-950 p-5 shadow-2xl shadow-black/40" data-testid="stadium-timeline">
            <h3 className="font-black uppercase text-sm tracking-wider mb-4">🏗️ Livelli disponibili</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
              {stadiumLevels.map((lvl, i) => {
                const num = i + 1;
                const owned = num <= currentLevel;
                const isNext = num === currentLevel + 1;
                return (
                  <div key={num}
                    className={`rounded-2xl border p-4 transition-all ${
                      owned ? "border-emerald-500/40 bg-emerald-950/30 shadow-emerald-500/10 shadow-lg"
                      : isNext ? "border-orange-500/50 bg-orange-950/20 shadow-orange-500/10 shadow-lg -translate-y-1"
                      : "border-slate-800 bg-slate-950/50 opacity-70"
                    }`}
                    data-testid={`stadium-level-${num}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-2xl">{owned ? "🏟️" : isNext ? "🏗️" : "🔒"}</div>
                      <div className={`text-[10px] font-black px-2 py-0.5 rounded-full ${
                        owned ? "bg-emerald-500/20 text-emerald-300"
                        : isNext ? "bg-orange-500/20 text-orange-300"
                        : "bg-slate-800 text-slate-500"
                      }`}>
                        {owned ? "✓ Attivo" : isNext ? "🎯 Prossimo" : <><Lock size={9} className="inline"/> Bloccato</>}
                      </div>
                    </div>
                    <div className="font-black text-lg">Livello {num}</div>
                    <div className="mt-3 space-y-1 text-xs">
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500">💰 Costo</span>
                        <span className="font-mono font-bold">{money(lvl.cost)}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500">📈 Ricavo</span>
                        <span className="font-mono font-bold text-emerald-300">+{money(lvl.income)}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500">🔧 Manut.</span>
                        <span className="font-mono font-bold text-rose-300">-{money(lvl.maintenance || 0)}</span>
                      </div>
                      {lvl.home_bonus != null && (
                        <div className="flex items-center justify-between pt-1 mt-1 border-t border-slate-800/60">
                          <span className="text-slate-500">🏠 Bonus casa</span>
                          <span className="font-mono font-bold text-amber-300">+{Number(lvl.home_bonus).toFixed(1)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="text-[10px] text-slate-500 mt-3">
              💡 Il ricavo effettivo può aumentare grazie ai bonus stadio (piazzamento e ranking) configurabili in Admin → Finanze.
            </div>
            {isAdmin && (
              <div className="mt-4 pt-4 border-t border-slate-800 flex flex-wrap items-center gap-2" data-testid="admin-force-level">
                <span className="text-[10px] font-mono uppercase tracking-widest text-rose-300 flex items-center gap-1">
                  ⚙️ Modalità Admin: forza livello (bypass costo/fase)
                </span>
                {stadiumLevels.map((_, i) => {
                  const num = i + 1;
                  return (
                    <button key={num}
                      onClick={() => forceSetLevel(num)}
                      disabled={adminBusy || num === currentLevel}
                      data-testid={`admin-set-stadium-${num}`}
                      className={`px-2.5 py-1 rounded-md text-xs font-mono font-bold uppercase border transition-colors ${
                        num === currentLevel
                          ? "bg-slate-800 text-slate-500 border-slate-700 cursor-not-allowed"
                          : "bg-rose-500/10 hover:bg-rose-500/20 text-rose-300 border-rose-500/40"
                      }`}>
                      Lv {num}
                    </button>
                  );
                })}
                <button
                  onClick={() => {
                    const raw = window.prompt(`Imposta livello personalizzato (0-${stadiumLevels.length}):`, currentLevel.toString());
                    const n = Number(raw);
                    if (!isNaN(n) && n >= 0 && n <= stadiumLevels.length) forceSetLevel(n);
                  }}
                  disabled={adminBusy}
                  data-testid="admin-set-stadium-custom"
                  className="px-2.5 py-1 rounded-md text-xs font-mono bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700">
                  Custom…
                </button>
              </div>
            )}
          </div>

          {/* Classifica stadi lega */}
          <StadiumsLeaderboard financeData={financeData} teams={teams} />

          {/* Storico ricavi stadio (dalle stagioni archiviate) */}
          <StadiumHistoryChart history={history} selectedTeamId={selectedTeamId} teams={teams} />
        </>
      )}
    </div>
  );
}

function StadiumKpi({ icon, label, value, tone, prefix, big }) {
  const toneMap = {
    emerald: "border-emerald-500/40 text-emerald-300",
    rose: "border-rose-500/40 text-rose-300",
    amber: "border-amber-500/40 text-amber-300",
  };
  return (
    <div className={`rounded-2xl border bg-slate-950/60 p-3 ${toneMap[tone] || "border-slate-800 text-white"}`}>
      <div className="text-[9px] uppercase tracking-widest text-slate-500 font-bold flex items-center gap-1">
        <span>{icon}</span> {label}
      </div>
      <div className={`font-mono font-black mt-1 ${big ? "text-2xl" : "text-lg"}`}>
        {prefix}{value}<span className="text-[9px] text-slate-500 ml-1">cr</span>
      </div>
    </div>
  );
}

function StadiumsLeaderboard({ financeData, teams }) {
  if (!financeData?.teams) return null;
  const rows = financeData.teams.map(f => {
    const t = teams.find(x => x.team_id === f.team_id);
    return {
      team_id: f.team_id, name: f.team_name, logo: t?.logo_url,
      level: f.stadium.level, base: f.stadium.base_income,
      net: f.stadium.net_income, bonus: f.stadium.bonus_multiplier,
    };
  }).sort((a,b) => b.net - a.net);
  return (
    <div className="border border-slate-800 rounded-3xl bg-gradient-to-br from-slate-900 to-slate-950 p-5 shadow-2xl shadow-black/40" data-testid="stadiums-leaderboard">
      <h3 className="font-black uppercase text-sm tracking-wider mb-3">🏆 Classifica ricavi stadio</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">#</th>
              <th className="text-left px-3 py-2">Squadra</th>
              <th className="text-center px-3 py-2">Livello</th>
              <th className="text-right px-3 py-2">Base</th>
              <th className="text-right px-3 py-2">Bonus</th>
              <th className="text-right px-3 py-2">Netto</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.team_id} className="border-t border-slate-800 hover:bg-slate-800/30" data-testid={`stadium-row-${r.team_id}`}>
                <td className="px-3 py-2 font-black text-slate-500">{i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `${i+1}°`}</td>
                <td className="px-3 py-2 font-bold flex items-center gap-2">
                  {logoSrc(r.logo)
                    ? <img src={logoSrc(r.logo)} alt="" className="w-5 h-5 rounded object-cover"/>
                    : <span className="w-5 h-5 rounded bg-slate-800 flex items-center justify-center text-[10px]">⚽</span>}
                  {r.name}
                </td>
                <td className="text-center px-3 py-2 font-mono">Lv {r.level}</td>
                <td className="text-right px-3 py-2 font-mono">{money(r.base)}</td>
                <td className="text-right px-3 py-2 font-mono text-amber-300">+{(r.bonus*100).toFixed(0)}%</td>
                <td className="text-right px-3 py-2 font-mono font-black text-emerald-300">{money(r.net)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function StadiumHistoryChart({ history, selectedTeamId, teams }) {
  const chartData = useMemo(() => {
    if (!history || !selectedTeamId) return [];
    const asc = [...history].sort((a, b) => a.season - b.season);
    return asc.map(s => {
      const t = (s.teams || []).find(x => x.team_id === selectedTeamId);
      const stadium = t?.finance?.stadium || {};
      return {
        season: `S${s.season}`,
        "Ricavo netto": Math.round(Number(stadium.net_income || t?.stadium || 0)),
        "Ricavo base": Math.round(Number(stadium.base_income || 0)),
        "Manutenzione": -Math.abs(Math.round(Number(stadium.maintenance || 0))),
        level: stadium.level || 0,
      };
    });
  }, [history, selectedTeamId]);

  const hasData = chartData.length > 0 && chartData.some(r => r["Ricavo netto"] !== 0 || r["Ricavo base"] !== 0);
  const teamName = teams.find(t => t.team_id === selectedTeamId)?.name || "";

  return (
    <div className="border border-slate-800 rounded-3xl bg-gradient-to-br from-slate-900 to-slate-950 p-5 shadow-2xl shadow-black/40" data-testid="stadium-history-chart">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <History size={16} className="text-orange-400"/>
          <h3 className="font-black uppercase text-sm tracking-wider">📈 Storico ricavi stadio — {teamName}</h3>
        </div>
        <span className="text-xs text-slate-500">{chartData.length} stagioni archiviate</span>
      </div>
      {hasData ? (
        <div className="h-64" data-testid="stadium-history-chart-container">
          <ResponsiveContainer>
            <AreaChart data={chartData} margin={{ top: 10, right: 20, bottom: 10, left: 0 }}>
              <defs>
                <linearGradient id="grStNet" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#F97316" stopOpacity={0.8}/>
                  <stop offset="100%" stopColor="#F97316" stopOpacity={0.05}/>
                </linearGradient>
                <linearGradient id="grStBase" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#FBBF24" stopOpacity={0.5}/>
                  <stop offset="100%" stopColor="#FBBF24" stopOpacity={0.02}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis dataKey="season" tick={{ fill: '#94a3b8', fontSize: 12, fontFamily: 'monospace' }}/>
              <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }}/>
              <Tooltip
                contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 12, fontSize: 12 }}
                formatter={(v, name) => [`${money(v)} cr`, name]}
                labelFormatter={(label, payload) => {
                  const lvl = payload?.[0]?.payload?.level;
                  return `${label} · Livello ${lvl || "?"}`;
                }}
              />
              <Area type="monotone" dataKey="Ricavo base" stroke="#FBBF24" strokeWidth={2} fill="url(#grStBase)" isAnimationActive animationDuration={900}/>
              <Area type="monotone" dataKey="Ricavo netto" stroke="#F97316" strokeWidth={2.5} fill="url(#grStNet)" isAnimationActive animationDuration={900}/>
            </AreaChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="text-center text-slate-500 text-sm py-10 border border-dashed border-slate-800 rounded-2xl">
          🏟️ Nessuna stagione archiviata. Il grafico si popolerà dopo il primo <strong className="text-orange-300">Applica saldo</strong> in Finanze.
        </div>
      )}
    </div>
  );
}
